import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { productsApi } from '../../../api/products.js';
import { categoriesApi } from '../../../api/categories.js';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = {
  sku: '',
  name: '',
  brand: '',
  description: '',
  speciesIds: [],
  breedIds: [],
  categoryIds: [],
  ageMin: '',
  ageMax: '',
  weightMin: '',
  weightMax: '',
  priceAmount: '',
  priceTaxIncluded: 'true',
  storeIds: [],
  ingredients: [],
  tags: [],
  status: 'active'
};

function num(v) {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function ProductFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();

  // Inventory map editable separately from the rest of the payload.
  const [inventory, setInventory] = useState({});

  const speciesQuery = useQuery({ queryKey: ['cat', 'species'], enabled: open, queryFn: () => categoriesApi.list({ kind: 'species', limit: 100, active: true }) });
  const breedQuery = useQuery({ queryKey: ['cat', 'breed'], enabled: open, queryFn: () => categoriesApi.list({ kind: 'breed', limit: 100, active: true }) });
  const productCatQuery = useQuery({ queryKey: ['cat', 'productCategory'], enabled: open, queryFn: () => categoriesApi.list({ kind: 'productCategory', limit: 100, active: true }) });
  const storesQuery = useQuery({ queryKey: ['stores', 'all'], enabled: open, queryFn: () => storesApi.list({ limit: 100 }) });

  const speciesOpts = useMemo(() => ((speciesQuery.data && speciesQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [speciesQuery.data]);
  const breedOpts = useMemo(() => ((breedQuery.data && breedQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [breedQuery.data]);
  const productCatOpts = useMemo(() => ((productCatQuery.data && productCatQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [productCatQuery.data]);
  const storeOpts = useMemo(() => ((storesQuery.data && storesQuery.data.items) || []).map((s) => ({ value: s._id, label: s.name, hint: s.code })), [storesQuery.data]);
  const storeMap = useMemo(() => new Map(((storesQuery.data && storesQuery.data.items) || []).map((s) => [String(s._id), s])), [storesQuery.data]);

  const { register, handleSubmit, reset, watch, control, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const watchedStoreIds = watch('storeIds');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        sku: initial.sku || '',
        name: initial.name || '',
        brand: initial.brand || '',
        description: initial.description || '',
        speciesIds: (initial.speciesIds || []).map((x) => x._id || x),
        breedIds: (initial.breedIds || []).map((x) => x._id || x),
        categoryIds: (initial.categoryIds || []).map((x) => x._id || x),
        ageMin: initial.ageRangeMonths?.min ?? '',
        ageMax: initial.ageRangeMonths?.max ?? '',
        weightMin: initial.weightRangeKg?.min ?? '',
        weightMax: initial.weightRangeKg?.max ?? '',
        priceAmount: initial.price?.amount ?? '',
        priceTaxIncluded: String(initial.price?.taxIncluded !== false),
        storeIds: (initial.storeIds || []).map((x) => x._id || x),
        ingredients: initial.ingredients || [],
        tags: initial.tags || [],
        status: initial.status || 'active'
      });
      const inv = {};
      for (const row of initial.inventory || []) {
        inv[String(row.storeId._id || row.storeId)] = {
          quantity: row.quantity ?? 0,
          reorderPoint: row.reorderPoint
        };
      }
      setInventory(inv);
    } else {
      reset(EMPTY);
      setInventory({});
    }
  }, [open, initial, reset]);

  // When the user removes a store from storeIds, drop its inventory row.
  useEffect(() => {
    const allowed = new Set((watchedStoreIds || []).map(String));
    setInventory((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        if (allowed.has(k)) next[k] = v;
      }
      return next;
    });
  }, [watchedStoreIds]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const inventoryArr = (values.storeIds || [])
        .map((sid) => {
          const row = inventory[String(sid)];
          if (!row) return null;
          const item = { storeId: sid, quantity: Number(row.quantity) || 0 };
          if (row.reorderPoint !== '' && row.reorderPoint !== undefined && row.reorderPoint !== null) {
            item.reorderPoint = Number(row.reorderPoint);
          }
          return item;
        })
        .filter(Boolean);

      const payload = {
        sku: values.sku.toUpperCase(),
        name: values.name,
        brand: values.brand || undefined,
        description: values.description || undefined,
        speciesIds: values.speciesIds,
        breedIds: values.breedIds,
        categoryIds: values.categoryIds,
        ageRangeMonths: { min: num(values.ageMin), max: num(values.ageMax) },
        weightRangeKg: { min: num(values.weightMin), max: num(values.weightMax) },
        ingredients: values.ingredients,
        tags: values.tags,
        price: {
          amount: Number(values.priceAmount),
          currency: 'EUR',
          taxIncluded: values.priceTaxIncluded === 'true'
        },
        storeIds: values.storeIds,
        inventory: inventoryArr,
        status: values.status
      };

      // Clean optional empty range objects so the server keeps the
      // current values instead of overwriting with nulls.
      if (!payload.ageRangeMonths.min && payload.ageRangeMonths.min !== 0 && !payload.ageRangeMonths.max && payload.ageRangeMonths.max !== 0) {
        delete payload.ageRangeMonths;
      }
      if (!payload.weightRangeKg.min && payload.weightRangeKg.min !== 0 && !payload.weightRangeKg.max && payload.weightRangeKg.max !== 0) {
        delete payload.weightRangeKg;
      }
      for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];

      return editing ? productsApi.update(initial._id, payload) : productsApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Prodotto aggiornato.' : 'Prodotto creato.');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  const updateInventoryQty = (sid, value) => {
    setInventory((prev) => ({ ...prev, [String(sid)]: { ...prev[String(sid)], quantity: value } }));
  };
  const updateReorder = (sid, value) => {
    setInventory((prev) => ({ ...prev, [String(sid)]: { ...prev[String(sid)], reorderPoint: value } }));
  };

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica ${initial?.name}` : 'Nuovo prodotto'}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="product-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="product-form" className="flex flex-col gap-6" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="SKU"
            hint="maiuscolo, cifre, - o _ (2-64)"
            error={errors.sku?.message}
            disabled={editing}
            {...register('sku', editing ? {} : {
              required: 'SKU richiesto',
              pattern: { value: /^[A-Z0-9_-]{2,64}$/i, message: 'formato non valido' }
            })}
          />
          <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
          <Input label="Brand" error={errors.brand?.message} {...register('brand')} />
          <Select label="Stato" error={errors.status?.message} {...register('status')}>
            <option value="active">Attivo</option>
            <option value="draft">Bozza</option>
            <option value="archived">Archiviato</option>
          </Select>
          <div className="sm:col-span-2">
            <Textarea label="Descrizione" rows={3} error={errors.description?.message} {...register('description')} />
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Controller
            control={control}
            name="speciesIds"
            render={({ field }) => <MultiSelect label="Specie" options={speciesOpts} value={field.value} onChange={field.onChange} />}
          />
          <Controller
            control={control}
            name="breedIds"
            render={({ field }) => <MultiSelect label="Razze" options={breedOpts} value={field.value} onChange={field.onChange} />}
          />
          <Controller
            control={control}
            name="categoryIds"
            render={({ field }) => <MultiSelect label="Categorie prodotto" options={productCatOpts} value={field.value} onChange={field.onChange} />}
          />
        </section>

        <section className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Input type="number" label="Età min (mesi)" min={0} {...register('ageMin')} />
          <Input type="number" label="Età max (mesi)" min={0} {...register('ageMax')} />
          <Input type="number" label="Peso min (kg)" step="0.1" min={0} {...register('weightMin')} />
          <Input type="number" label="Peso max (kg)" step="0.1" min={0} {...register('weightMax')} />
        </section>

        <section className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Input
            type="number"
            label="Prezzo (EUR)"
            min={0}
            step="0.01"
            error={errors.priceAmount?.message}
            {...register('priceAmount', { required: 'Prezzo richiesto', min: 0 })}
          />
          <Select label="IVA inclusa" {...register('priceTaxIncluded')}>
            <option value="true">Sì</option>
            <option value="false">No</option>
          </Select>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            control={control}
            name="ingredients"
            render={({ field }) => <TagInput label="Ingredienti" value={field.value} onChange={field.onChange} placeholder="Aggiungi e premi Invio" />}
          />
          <Controller
            control={control}
            name="tags"
            render={({ field }) => <TagInput label="Tag" value={field.value} onChange={field.onChange} placeholder="es. premium, no-cereali" lowercase />}
          />
        </section>

        <section>
          <Controller
            control={control}
            name="storeIds"
            rules={{ validate: (v) => (v && v.length > 0) || 'Selezionare almeno un negozio' }}
            render={({ field }) => (
              <MultiSelect
                label="Negozi che vendono il prodotto"
                options={storeOpts}
                value={field.value}
                onChange={field.onChange}
                error={errors.storeIds?.message}
              />
            )}
          />

          {(watchedStoreIds || []).length > 0 && (
            <div className="mt-3 overflow-hidden rounded-md border border-border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-surface">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Negozio</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Quantità</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Punto di riordino</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {watchedStoreIds.map((sid) => {
                    const s = storeMap.get(String(sid));
                    const row = inventory[String(sid)] || { quantity: '', reorderPoint: '' };
                    return (
                      <tr key={String(sid)}>
                        <td className="px-3 py-2 text-sm">{s ? `${s.name} (${s.code})` : String(sid)}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={row.quantity ?? ''}
                            onChange={(e) => updateInventoryQty(sid, e.target.value)}
                            className="h-8 w-24 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={row.reorderPoint ?? ''}
                            onChange={(e) => updateReorder(sid, e.target.value)}
                            className="h-8 w-24 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </form>
    </Modal>
  );
}
